import { Link } from "react-router-dom";
import { Clock, MapPin, MessageCircle, ExternalLink, CalendarX, Info } from "lucide-react";
import { SITE, HOURS, BRANCHES } from "../data/site";
import { VertKanji } from "../components/Chrome";

function BranchRow({ branch, index }) {
  return (
    <article
      data-testid={`branch-card-${branch.id}`}
      className="grid gap-6 border-b border-sand py-10 first:border-t lg:grid-cols-12"
    >
      <div className="lg:col-span-1">
        <span aria-hidden="true" className="font-display text-3xl font-bold text-salmon/60">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
      <div className="lg:col-span-6">
        <h2 className="font-display text-2xl font-bold text-ink">
          {branch.area}
          {branch.isNew ? (
            <span className="ml-3 inline-block border border-wasabi/50 bg-wasabi/10 px-2.5 py-1 align-middle text-[11px] font-bold uppercase tracking-widest text-wasabi">
              Cabang Baru
            </span>
          ) : null}
        </h2>
        <p className="mt-1 text-sm font-semibold uppercase tracking-[0.2em] text-soy">{branch.city}</p>
        <p className="mt-4 flex items-start gap-2.5 text-sm leading-relaxed text-sumi">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-hanko" aria-hidden="true" />
          {branch.address}
        </p>
        <p className="mt-3 flex items-start gap-2.5 text-sm text-sumi">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-hanko" aria-hidden="true" />
          {HOURS.summary}
        </p>
        {branch.note ? (
          <p className="mt-3 flex items-start gap-2.5 text-xs italic leading-relaxed text-sumi/80">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {branch.note}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col justify-center gap-3 lg:col-span-5 lg:items-end">
        <a
          href={branch.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`branch-directions-${branch.id}`}
          className="inline-flex w-full items-center justify-center gap-2 border-2 border-ink px-5 py-3 text-sm font-bold text-ink transition-colors duration-200 hover:bg-ink hover:text-paper sm:w-auto"
        >
          <MapPin className="h-4 w-4" aria-hidden="true" />
          Petunjuk Arah
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
        {branch.whatsapp ? (
          <a
            href={branch.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`branch-whatsapp-${branch.id}`}
            className="inline-flex w-full items-center justify-center gap-2 bg-hanko px-5 py-3 text-sm font-bold text-paper transition-colors duration-200 hover:bg-[#a82719] sm:w-auto"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            WhatsApp / Waiting List
          </a>
        ) : (
          <a
            href={SITE.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`branch-instagram-${branch.id}`}
            className="inline-flex w-full items-center justify-center gap-2 text-sm font-bold text-hanko underline underline-offset-4 transition-colors hover:text-ink sm:w-auto"
          >
            Info terbaru via Instagram
          </a>
        )}
      </div>
    </article>
  );
}

export default function LokasiPage() {
  return (
    <>
      <section className="border-b-2 border-ink bg-parchment" data-testid="lokasi-hero">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
          <nav aria-label="Breadcrumb" className="text-xs font-semibold uppercase tracking-[0.25em] text-sumi">
            <Link to="/" data-testid="breadcrumb-home" className="transition-colors hover:text-hanko">
              Beranda
            </Link>
            <span aria-hidden="true"> / </span>
            <span className="text-hanko">Lokasi</span>
          </nav>
          <div className="mt-6 flex items-start justify-between gap-6">
            <div>
              <h1 className="font-display text-4xl font-bold tracking-tight text-ink sm:text-5xl">
                Lokasi Cabang Umaku Sushi
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-sumi">
                Empat cabang di Jabodetabek — masing-masing dengan suasana kedai Jepang yang simpel dan minimalis.
                Pilih yang terdekat dari Anda.
              </p>
            </div>
            <VertKanji text="店舗案内" className="hidden text-2xl font-bold text-ink/15 lg:block" />
          </div>
          <div
            data-testid="lokasi-hours-banner"
            className="mt-10 inline-flex flex-wrap items-center gap-x-6 gap-y-2 border-2 border-ink bg-paper px-6 py-4 shadow-[6px_6px_0_#E4DFD3]"
          >
            <span className="inline-flex items-center gap-2 text-sm font-bold text-ink">
              <Clock className="h-4 w-4 text-hanko" aria-hidden="true" />
              {HOURS.days} · {HOURS.time}
            </span>
            <span aria-hidden="true" className="hidden h-4 w-px bg-ink/20 sm:block" />
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-sumi">
              <CalendarX className="h-4 w-4 text-hanko" aria-hidden="true" />
              Senin seluruh cabang tutup
            </span>
          </div>
        </div>
      </section>

      <section className="bg-paper py-14 lg:py-20" data-testid="lokasi-list">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          {BRANCHES.map((branch, i) => (
            <BranchRow key={branch.id} branch={branch} index={i} />
          ))}
          <p className="mt-10 max-w-2xl text-xs italic leading-relaxed text-sumi/80">
            Informasi operasional dapat berubah pada hari libur nasional atau momen tertentu. Untuk kepastian jam
            buka dan ketersediaan tempat, silakan cek Instagram {SITE.instagramHandle} sebelum berkunjung.
          </p>
        </div>
      </section>
    </>
  );
}
